import { Flex, Spacer, useToast } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  GetFundingVerificationPipelineRunsDocumentGQLType,
  useNotifyVerificationReportReadyForReviewMutation,
} from '../requests/requests.gql.generated';
import { VerificationReportTakeActionButton } from './VerificationReportTakeActionButton';
import { useVerificationOptions } from './VerificationTableSharedComponents';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { GetFundingRequestsWithEventDetailsDocumentGQLType } from 'packages/funding-requests/requests/requests.gql.generated';

type VerificationNotifyReportReadyToReviewActionProps = {
  verificationReportId: string;
  capitalProviderNames: string[];
};
export const VerificationNotifyReportReadyToReviewAction = ({
  verificationReportId,
  capitalProviderNames,
}: VerificationNotifyReportReadyToReviewActionProps) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { navigator } = useVerificationOptions();

  const isSingleCapitalProvider = capitalProviderNames.length === 1;
  const capitalProviderNamesString = isSingleCapitalProvider
    ? capitalProviderNames[0]
    : 'Your capital providers';

  const [
    notifyVerificationReportReadyForReview,
  ] = useNotifyVerificationReportReadyForReviewMutation({
    onCompleted: (data) => {
      if (data && data.notifyVerificationReportReadyForReview) {
        toast({
          ...successToastConfig,
          id: 'notifyVerificationReportReadyForReview',
          title: `${capitalProviderNamesString} ${
            isSingleCapitalProvider ? 'has' : 'have'
          } been notified`,
        });
        navigator(navigate).reset();
      }
    },
    onError: () => {
      toast({
        ...errorToastConfig,
        id: 'notifyVerificationReportReadyForReview',
        title: 'Could not send notification. Please try again.',
      });
    },
    refetchQueries: [
      GetFundingRequestsWithEventDetailsDocumentGQLType,
      GetFundingVerificationPipelineRunsDocumentGQLType,
    ],
    awaitRefetchQueries: true,
  });

  const [pendingOperation, setPendingOperation] = useState<'none' | 'notify'>(
    'none',
  );

  const onClickNotify = useCallback(async () => {
    try {
      setPendingOperation('notify');
      await notifyVerificationReportReadyForReview({
        variables: {
          verificationReportId,
        },
      });
    } finally {
      setPendingOperation('none');
    }
  }, [notifyVerificationReportReadyForReview, verificationReportId]);

  return (
    <Flex mt="40px">
      <Spacer />
      <VerificationReportTakeActionButton
        pendingOperation={pendingOperation}
        onClickConfirmAction={onClickNotify}
        headerText="Confirm Submit for Review"
        bodyText={`Are you sure you want to notify ${
          isSingleCapitalProvider
            ? capitalProviderNamesString
            : _.lowerCase(capitalProviderNamesString)
        } that this report is ready for review?`}
        confirmActionButtonText="Submit for Review"
        mainActionButtonStyle="fill"
      />
    </Flex>
  );
};
